import type { EventName } from "../types"

/**
 * Sampling configuration type mapping event names to their sample rates
 * Sample rate is a number between 0 and 1 where:
 * - 0 = never sample (0%)
 * - 0.1 = sample 10% of events
 * - 0.5 = sample 50% of events
 * - 1 = always sample (100%)
 */
type SamplingConfig = Partial<Record<EventName, number>>

/**
 * Default sample rate when no specific rate is configured for an event
 */
export const DEFAULT_SAMPLE_RATE = 1.0

/**
 * Sampling configuration for events
 */
export const DEFAULT_SAMPLING_CONFIG: SamplingConfig = {
  "Search Started": 1,
  "Search Result Clicked": 1,
  "Search Filter Applied": 1,
  "Search Category Selected": 1,
  "Recent Search Selected": 1,
  "Search Dismissed": 1,
}

/**
 * Get sampling configuration from environment variables if available
 * Allows overriding default sample rates via NEXT_PUBLIC_SAMPLING_* env vars
 */
export function getSamplingConfig(): SamplingConfig {
  if (typeof window === "undefined") {
    return DEFAULT_SAMPLING_CONFIG
  }

  const config = { ...DEFAULT_SAMPLING_CONFIG }

  // Allow environment variable overrides
  // Example: NEXT_PUBLIC_SAMPLING_SEARCH_STARTED=0.5
  if (process.env.NEXT_PUBLIC_SAMPLING_SEARCH_STARTED) {
    config["Search Started"] = Number.parseFloat(
      process.env.NEXT_PUBLIC_SAMPLING_SEARCH_STARTED,
    )
  }

  return config
}

/**
 * Determines whether an event should be sampled based on the configured rate
 * @param eventName - The name of the event to potentially sample
 * @param config - Optional sampling configuration (defaults to getSamplingConfig())
 * @returns true if the event should be tracked, false if it should be skipped
 */
export function shouldSample(
  eventName: EventName,
  config?: SamplingConfig,
): boolean {
  const samplingConfig = config ?? getSamplingConfig()
  const sampleRate = samplingConfig[eventName] ?? DEFAULT_SAMPLE_RATE

  // Always sample if rate is 1 or higher
  if (sampleRate >= 1) {
    return true
  }

  // Never sample if rate is 0 or lower
  if (sampleRate <= 0) {
    return false
  }

  // Random sampling based on the configured rate
  return Math.random() < sampleRate
}

/**
 * Options for tracking events with sampling controls
 */
export type SamplingOptions = {
  /**
   * Force this event to be tracked regardless of sampling configuration
   * Use for critical events that must always be tracked
   */
  forceSample?: boolean
  /**
   * Custom sample rate for this specific event instance
   * Overrides the default configuration
   */
  sampleRate?: number
}

/**
 * Determines whether an event should be sampled with custom options
 */
export function shouldSampleWithOptions(
  eventName: EventName,
  options?: SamplingOptions,
): boolean {
  if (options?.forceSample) {
    return true
  }

  if (options?.sampleRate !== undefined) {
    if (options.sampleRate >= 1) {
      return true
    }
    if (options.sampleRate <= 0) {
      return false
    }
    return Math.random() < options.sampleRate
  }

  return shouldSample(eventName)
}
